/**
 * Video Settings - Super-Admin only
 * Manage virtual background images for video consultations.
 */
import { useState, useRef } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  Video,
  Upload,
  Trash2,
  Plus,
  Loader2,
  ImageIcon,
  ArrowUp,
  ArrowDown,
  Stamp,
  X,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Skeleton,
  useToast,
} from "@jourfx/ui";
import { SuperAdminGuard } from "../components/SuperAdminGuard";
import { PageHeader } from "../components/PageHeader";
import { LogoOverlayModal } from "../components/LogoOverlayModal";

const MAX_BACKGROUNDS = 5;

function VideoSettingsContent() {
  const { toast } = useToast();

  const backgrounds = useQuery(api.admin.videoBackgrounds.list);
  const generateUploadUrl = useMutation(api.admin.videoBackgrounds.generateUploadUrl);
  const create = useMutation(api.admin.videoBackgrounds.create);
  const remove = useMutation(api.admin.videoBackgrounds.remove);
  const reorder = useMutation(api.admin.videoBackgrounds.reorder);

  const branding = useQuery(api.admin.branding.getAdmin);
  const removeOverlay = useMutation(api.admin.videoBackgrounds.removeOverlay);

  const [showUploadForm, setShowUploadForm] = useState(false);
  const [name, setName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [overlayTarget, setOverlayTarget] = useState<{
    id: string;
    imageUrl: string;
    overlay?: { corner: "top-left" | "top-right" | "bottom-left" | "bottom-right"; sizePct: number; opacityPct: number } | null;
  } | null>(null);
  const [isRemovingOverlay, setIsRemovingOverlay] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const hasLogo = !!(
    branding?.assets?.logoPrimaryStorageId ||
    branding?.assets?.logoSquareStorageId
  );

  const count = backgrounds?.length ?? 0;
  const isFull = count >= MAX_BACKGROUNDS;

  const handleUpload = async () => {
    if (!selectedFile || !name.trim()) return;

    setIsUploading(true);
    try {
      const url = await generateUploadUrl({});

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": selectedFile.type },
        body: selectedFile,
      });

      if (!res.ok) {
        throw new Error(`Upload fehlgeschlagen (Status ${res.status})`);
      }

      const json = (await res.json()) as { storageId?: string };
      if (!json.storageId) {
        throw new Error("Keine Storage-ID erhalten");
      }

      await create({
        name: name.trim(),
        storageId: json.storageId as any,
      });

      toast({
        title: "Hintergrundbild hinzugefügt",
        description: `"${name.trim()}" wurde erfolgreich hochgeladen.`,
      });

      // Reset form
      setName("");
      setSelectedFile(null);
      setShowUploadForm(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(id);
    try {
      await remove({ id: id as any });
      toast({
        title: "Hintergrundbild entfernt",
        description: "Das Hintergrundbild wurde erfolgreich gelöscht.",
      });
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleReorder = async (id: string, direction: "up" | "down") => {
    if (!backgrounds) return;
    setIsReordering(true);
    try {
      const currentIndex = backgrounds.findIndex((bg: any) => bg._id === id);
      const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= backgrounds.length) return;

      const newOrder = backgrounds.map((bg: any) => bg._id as string);
      [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];

      await reorder({ ids: newOrder as any });
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setIsReordering(false);
    }
  };

  const handleCancel = () => {
    setShowUploadForm(false);
    setName("");
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveOverlay = async (id: string) => {
    setIsRemovingOverlay(id);
    try {
      await removeOverlay({ id: id as any });
      toast({
        title: "Logo-Overlay entfernt",
        description: "Das Hintergrundbild wurde wiederhergestellt.",
      });
    } catch (error) {
      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setIsRemovingOverlay(null);
    }
  };

  // Loading state
  if (backgrounds === undefined) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Video-Einstellungen"
        description="Hintergrundbilder für Videosprechstunden verwalten"
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Video className="h-5 w-5" />
                Virtuelle Hintergrundbilder
              </CardTitle>
              <CardDescription>
                {count} von {MAX_BACKGROUNDS} Hintergrundbildern verwendet
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowUploadForm(true)}
              disabled={isFull || showUploadForm}
            >
              <Plus className="mr-2 h-4 w-4" />
              Hinzufügen
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Upload form */}
          {showUploadForm && (
            <div className="rounded-lg border border-dashed p-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bg-name">Name</Label>
                <Input
                  id="bg-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="z.B. Büro, Natur, Neutral"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bg-file">Bilddatei</Label>
                <input
                  ref={fileInputRef}
                  id="bg-file"
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleUpload}
                  disabled={isUploading || !name.trim() || !selectedFile}
                >
                  {isUploading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Hochladen
                </Button>
                <Button variant="outline" onClick={handleCancel} disabled={isUploading}>
                  Abbrechen
                </Button>
              </div>
            </div>
          )}

          {/* Background list */}
          {count === 0 && !showUploadForm ? (
            <div className="flex flex-col items-center justify-center py-12">
              <ImageIcon className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium text-foreground">
                Keine Hintergrundbilder
              </h3>
              <p className="mt-1 text-sm text-muted-foreground text-center max-w-sm">
                Es sind noch keine virtuellen Hintergrundbilder konfiguriert. Fügen Sie
                bis zu {MAX_BACKGROUNDS} Bilder hinzu.
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {backgrounds.map((bg: any, index: number) => (
                <div
                  key={bg._id}
                  className="flex items-center gap-4 rounded-lg border p-3"
                >
                  <div className="relative">
                    {bg.url ? (
                      <img
                        src={bg.url}
                        alt={bg.name}
                        className="w-24 h-16 rounded object-cover"
                      />
                    ) : (
                      <div className="w-24 h-16 rounded bg-muted flex items-center justify-center">
                        <ImageIcon className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    {bg.logoOverlay && (
                      <span
                        className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground"
                        title="Logo-Overlay aktiv"
                      >
                        <Stamp className="h-3 w-3" />
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{bg.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Position {index + 1}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={!hasLogo}
                      onClick={() =>
                        setOverlayTarget({
                          id: bg._id,
                          imageUrl: bg.originalUrl ?? bg.url,
                          overlay: bg.logoOverlay,
                        })
                      }
                      aria-label="Logo hinzufügen"
                      title={
                        hasLogo
                          ? "Logo-Overlay hinzufügen"
                          : "Erst ein Logo in den Branding-Einstellungen hochladen"
                      }
                    >
                      <Stamp className="h-4 w-4" />
                    </Button>
                    {bg.logoOverlay && (
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={isRemovingOverlay === bg._id}
                        onClick={() => handleRemoveOverlay(bg._id)}
                        aria-label="Logo entfernen"
                        title="Logo-Overlay entfernen"
                      >
                        {isRemovingOverlay === bg._id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={index === 0 || isReordering}
                      onClick={() => handleReorder(bg._id, "up")}
                      aria-label="Nach oben"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={index === count - 1 || isReordering}
                      onClick={() => handleReorder(bg._id, "down")}
                      aria-label="Nach unten"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={isDeleting === bg._id}
                      onClick={() => handleDelete(bg._id)}
                      aria-label="Löschen"
                    >
                      {isDeleting === bg._id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-destructive" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {overlayTarget && (
        <LogoOverlayModal
          open={!!overlayTarget}
          onOpenChange={(open) => {
            if (!open) setOverlayTarget(null);
          }}
          backgroundId={overlayTarget.id}
          imageUrl={overlayTarget.imageUrl}
          existingOverlay={overlayTarget.overlay}
        />
      )}
    </div>
  );
}

export function VideoSettings() {
  return (
    <SuperAdminGuard>
      <VideoSettingsContent />
    </SuperAdminGuard>
  );
}
